/* Luma Nordic — theme scripts (no dependencies) */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const overlay = $('[data-overlay]');
  const toast = $('[data-toast]');

  /* ---------- Drawers & modals ---------- */
  let openPanel = null;
  let lastFocus = null;

  function openDrawer(el) {
    if (!el) return;
    if (openPanel && openPanel !== el) closeDrawer(openPanel, false);
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.add('is-visible'));
    document.body.classList.add('is-locked');
    openPanel = el;
    setTimeout(() => el.focus({ preventScroll: true }), 50);
  }

  function closeDrawer(el = openPanel, restoreFocus = true) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-visible');
    setTimeout(() => { if (!openPanel) overlay.hidden = true; }, 350);
    document.body.classList.remove('is-locked');
    openPanel = null;
    if (restoreFocus && lastFocus) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(document.getElementById(opener.dataset.drawerOpen)); return; }
    const modalOpener = e.target.closest('[data-modal-open]');
    if (modalOpener) { e.preventDefault(); openDrawer(document.getElementById(modalOpener.dataset.modalOpen)); return; }
    if (e.target.closest('[data-drawer-close], [data-modal-close]')) { closeDrawer(); return; }
    const cartOpener = e.target.closest('[data-cart-open]');
    if (cartOpener && window.theme.cartType === 'drawer' && $('#CartDrawer')) {
      e.preventDefault(); openDrawer($('#CartDrawer'));
    }
  });
  overlay?.addEventListener('click', () => closeDrawer());
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openPanel) closeDrawer(); });

  // Close a modal when clicking its backdrop
  document.addEventListener('click', (e) => {
    if (e.target.classList?.contains('modal') && e.target.classList.contains('is-open')) closeDrawer();
  });

  ['cart-drawer', 'menu-drawer', 'modal-dialog'].forEach((name) => {
    if (!customElements.get(name)) customElements.define(name, class extends HTMLElement {});
  });

  function showToast(message) {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(showToast.t);
    showToast.t = setTimeout(() => toast.classList.remove('is-visible'), 2600);
  }

  /* ---------- Header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const announcement = $('.announcement');
      const setOffset = () => {
        document.documentElement.style.setProperty('--announcement-height', `${announcement ? announcement.offsetHeight : 0}px`);
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      };
      setOffset();
      window.addEventListener('resize', setOffset, { passive: true });

      if (this.dataset.transparent === 'true') {
        const onScroll = () => this.classList.toggle('is-solid', window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      // Mega menu aria-expanded
      $$('.nav__item--mega', this).forEach((item) => {
        const link = $('.nav__link', item);
        item.addEventListener('mouseenter', () => link.setAttribute('aria-expanded', 'true'));
        item.addEventListener('mouseleave', () => link.setAttribute('aria-expanded', 'false'));
      });

      // Search bar
      const bar = $('[data-search-bar]', this);
      $('[data-search-open]', this)?.addEventListener('click', () => {
        bar.hidden = !bar.hidden;
        if (!bar.hidden) { this.classList.add('is-solid'); $('input[type="search"]', bar).focus(); }
      });
      $('[data-search-close]', this)?.addEventListener('click', () => { bar.hidden = true; });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      const items = $$('.announcement__item', this);
      if (items.length < 2) return;
      let i = 0;
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, Number(this.dataset.interval) || 5000);
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root = document) {
    const els = $$('.reveal:not(.is-visible)', root);
    if (!document.body.classList.contains('has-animations') || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  }
  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* ---------- Tabs & sliders ---------- */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      const tabs = $$('[role="tab"]', this);
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', selected);
          document.getElementById(t.getAttribute('aria-controls')).hidden = !selected;
        });
      }));
    }
  }
  customElements.define('product-tabs', ProductTabs);

  class SliderRow extends HTMLElement {
    connectedCallback() {
      const track = $('.slider__track', this);
      const prev = $('[data-slider-prev]', this);
      const next = $('[data-slider-next]', this);
      const step = () => (track.firstElementChild?.offsetWidth || 300) + 20;
      const update = () => {
        if (prev) prev.disabled = track.scrollLeft < 5;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      update();
    }
  }
  customElements.define('slider-row', SliderRow);

  /* ---------- Cart ---------- */
  async function refreshCart(sectionsHtml) {
    const drawer = $('#CartDrawer');
    if (drawer && sectionsHtml && sectionsHtml['cart-drawer']) {
      const doc = new DOMParser().parseFromString(sectionsHtml['cart-drawer'], 'text/html');
      const fresh = $('[data-cart-contents]', doc);
      if (fresh) $('[data-cart-contents]', drawer).replaceWith(fresh);
    }
    const cart = await fetch(`${window.theme.routes.cart_url}.js`).then((r) => r.json());
    $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
    return cart;
  }

  async function addToCart(variantId, quantity = 1, button) {
    button?.classList.add('is-loading');
    try {
      const res = await fetch(`${window.theme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: 'cart-drawer' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || window.theme.strings.error);
      await refreshCart(data.sections);
      if (window.theme.cartType === 'drawer' && $('#CartDrawer')) openDrawer($('#CartDrawer'));
      else showToast(window.theme.strings.added);
    } catch (err) {
      showToast(err.message || window.theme.strings.error);
    } finally {
      button?.classList.remove('is-loading');
    }
  }

  // Colour swatches on product cards: swap the card image to that colour
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.card .swatch[data-card-image]');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('.card');
    const img = $('.card__img--primary', card);
    if (img) { img.removeAttribute('srcset'); img.src = sw.dataset.cardImage; }
    $$('a.card__media, .card__title a', card).forEach((a) => { a.href = sw.href; });
    $$('.swatch', card).forEach((s) => s.classList.toggle('is-active', s === sw));
  });

  // Quick add from product cards
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add-variant]');
    if (!btn) return;
    e.preventDefault();
    addToCart(btn.dataset.addVariant, 1, btn);
  });

  // Quantity changes in the drawer
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const contents = $('[data-cart-contents]');
    contents?.classList.add('is-loading');
    try {
      const res = await fetch(`${window.theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(btn.dataset.lineIndex), quantity: Number(btn.dataset.qtyChange), sections: 'cart-drawer' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || window.theme.strings.error);
      await refreshCart(data.sections);
    } catch (err) {
      showToast(err.message);
      $('[data-cart-contents]')?.classList.remove('is-loading');
    }
  });

  /* ---------- Product page ---------- */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      this.variants = json ? JSON.parse(json.textContent).variants : [];
      this.form = $('form[id^="ProductForm"]', this);
      this.idInput = $('[data-variant-id]', this);
      this.addButton = $('[data-add-button]', this);
      this.stickyBar = $('[data-sticky-atc]', this);

      $$('fieldset.option input', this).forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        if (this.idInput.value) addToCart(this.idInput.value, 1, this.addButton);
      });
      this.markAvailability();
      this.initGallery();
      this.initSticky();
    }

    selectedOptions() {
      return $$('fieldset.option', this).map((fs) => $('input:checked', fs)?.value);
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      $$('fieldset.option', this).forEach((fs, i) => { const label = $('[data-option-value]', fs); if (label) label.textContent = selected[i]; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.markAvailability();
      // Always show the chosen colour, even if this exact size is unavailable
      const colorSet = $('fieldset.option--color', this);
      if (colorSet) {
        const ci = Number(colorSet.dataset.optionIndex);
        const byColor = this.variants.find((v) => v.options[ci] === selected[ci] && v.media);
        if (byColor && (!variant || !variant.media)) this.showMedia(byColor.media);
      }
      if (!variant) { this.setButton(false, window.theme.strings.soldOut); return; }

      this.idInput.value = variant.id;
      this.setButton(variant.available, variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut);
      const price = $('[data-price]', this);
      if (price) {
        price.classList.toggle('price--sale', !!variant.compare);
        price.innerHTML = variant.compare
          ? `<span class="price__current">${variant.price}</span><s class="price__compare">${variant.compare}</s>`
          : `<span class="price__current">${variant.price}</span>`;
      }
      const stickyPrice = $('[data-sticky-price]', this);
      if (stickyPrice) stickyPrice.textContent = variant.price;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      if (variant.media) this.showMedia(variant.media);
    }

    setButton(enabled, text) {
      if (this.addButton) { this.addButton.disabled = !enabled; $('[data-add-label]', this.addButton).textContent = text; }
      const sticky = $('[data-sticky-add]', this);
      if (sticky) { sticky.disabled = !enabled; sticky.textContent = text; }
    }

    // Grey out values that have no available variant given the other selections
    markAvailability() {
      const selected = this.selectedOptions();
      $$('fieldset.option', this).forEach((fs, idx) => {
        $$('input', fs).forEach((input) => {
          const test = [...selected]; test[idx] = input.value;
          const ok = this.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
          input.nextElementSibling?.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    initGallery() {
      const gallery = $('[data-gallery]', this);
      if (!gallery) return;
      this.slider = $('[data-slider]', gallery);
      this.slides = this.slider ? $$('.pgallery__slide', this.slider) : [];
      this.dots = $('[data-dots]', gallery);
      gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) this.showMedia(thumb.dataset.thumb);
        const dot = e.target.closest('[data-dot]');
        if (dot) this.goToSlide(Number(dot.dataset.dot));
      });
      if (!this.slider || this.slides.length < 2) { if (this.dots) this.dots.hidden = true; return; }
      if (this.dots) this.dots.innerHTML = this.slides.map((_, i) => `<span data-dot="${i}"></span>`).join('');
      // The finger moves the images natively (scroll-snap); we only follow which slide is showing
      let ticking = false;
      this.slider.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this.syncSlide(); });
      }, { passive: true });
      this.syncSlide();
    }

    currentSlide() {
      return this.slider ? Math.round(this.slider.scrollLeft / Math.max(1, this.slider.clientWidth)) : 0;
    }

    syncSlide() {
      const i = Math.min(this.slides.length - 1, Math.max(0, this.currentSlide()));
      const id = this.slides[i]?.dataset.mediaId;
      if (this.dots) $$('span', this.dots).forEach((d, k) => d.classList.toggle('is-active', k === i));
      $$('[data-thumb]', this).forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === id));
    }

    goToSlide(i, smooth = true) {
      if (!this.slider) return;
      this.slider.scrollTo({ left: i * this.slider.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    }

    // Slide to the given media (colour image or thumbnail)
    showMedia(mediaId) {
      const i = this.slides?.findIndex((s) => s.dataset.mediaId === String(mediaId)) ?? -1;
      if (i < 0) return;
      this.goToSlide(i);
      const top = this.slider.getBoundingClientRect().top;
      if (top < 0) this.slider.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    initSticky() {
      if (!this.stickyBar || !this.addButton || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.stickyBar.classList.toggle('is-visible', show);
        this.stickyBar.setAttribute('aria-hidden', String(!show));
      }).observe(this.addButton);
    }
  }
  customElements.define('product-page', ProductPage);

  /* ---------- Recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { io.disconnect(); this.load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else this.load();
    }
    async load() {
      const fetchHtml = async (url) => {
        const text = await fetch(url).then((r) => r.text());
        const el = $('product-recommendations', new DOMParser().parseFromString(text, 'text/html'));
        return el && el.innerHTML.trim().length ? el.innerHTML : '';
      };
      let html = await fetchHtml(this.dataset.url);
      if (!html && this.dataset.fallbackUrl && this.dataset.fallbackUrl !== this.dataset.url) html = await fetchHtml(this.dataset.fallbackUrl);
      if (html) { this.innerHTML = html; initReveal(this); } else this.closest('section')?.remove();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Collection filters ---------- */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = $('[data-filter-form]', this);
      this.sectionId = this.dataset.sectionId;
      this.form?.addEventListener('change', () => this.submit());
      $('#SortBy', this)?.addEventListener('change', () => this.submit());
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-filter-link]');
        if (!link) return;
        e.preventDefault();
        this.render(link.href);
      });
      window.addEventListener('popstate', () => this.render(window.location.href, false));

      // Grid toggle (2 / 4 columns), remembered per visitor
      const list = () => $('[data-grid-list]', this);
      let pref = '4';
      try { pref = localStorage.getItem('luma-grid') || '4'; } catch (e) {}
      const apply = (n) => {
        list()?.classList.toggle('is-large', n === '2');
        $$('[data-grid]', this).forEach((b) => b.classList.toggle('is-active', b.dataset.grid === n));
      };
      apply(pref);
      $$('[data-grid]', this).forEach((b) => b.addEventListener('click', () => {
        pref = b.dataset.grid; apply(pref);
        try { localStorage.setItem('luma-grid', pref); } catch (e) {}
      }));
      this.applyGrid = () => apply(pref);
    }

    submit() {
      const params = new URLSearchParams(new FormData(this.form));
      const sort = $('#SortBy', this)?.value;
      if (sort) params.set('sort_by', sort);
      for (const [key, value] of [...params.entries()]) if (value === '') params.delete(key);
      this.render(`${window.location.pathname}?${params.toString()}`);
    }

    async render(url, push = true) {
      const results = $('[data-results]', this);
      results?.classList.add('is-loading');
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      try {
        const html = await fetch(fetchUrl).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['[data-results]', '[data-filter-chips]', '.filters', '.toolbar__filter', '.toolbar__count', '.drawer__footer--split'].forEach((sel) => {
          const fresh = $(sel, doc); const current = $(sel, this);
          if (fresh && current) current.replaceWith(fresh);
        });
        this.applyGrid?.();
        initReveal(this);
        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          history.pushState({}, '', clean);
        }
      } catch (e) {
        window.location.href = url;
      } finally {
        $('[data-results]', this)?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('collection-filters', CollectionFilters);
})();
